// Tiny dependency-free IndexedDB wrapper for the demo data layer.
//
// Demo *business data* (inventory, customers, estimates, repairs, orders,
// analytics cache, AI history) lives here. Session counters / UI prefs / demo
// limits / onboarding state live in local/sessionStorage (see limits.js) — NOT
// here.

const DB_NAME = "jewelflow-demo";
// v2 adds the linked back-office stores (purchases, karigars, approval memos,
// payments, expenses, pipeline, gold-rate history).
// v3 adds bulk-inventory buckets/ledger and standalone purchase returns.
// Bump on any schema change.
const DB_VERSION = 3;

export const STORES = {
  inventory: "inventory",
  customers: "customers",
  suppliers: "suppliers",
  estimates: "estimates",
  repairs: "repairs",
  orders: "orders",
  purchases: "purchases",
  purchaseReturns: "purchaseReturns",
  karigars: "karigars",
  consignmentMemos: "consignmentMemos",
  payments: "payments",
  expenses: "expenses",
  bulkBuckets: "bulkBuckets",
  bulkLedger: "bulkLedger",
  pipelineLeads: "pipelineLeads",
  goldRates: "goldRates",
  goldRateHistory: "goldRateHistory",
  dashboardTasks: "dashboardTasks",
  analyticsCache: "analyticsCache",
  aiHistory: "aiHistory",
  meta: "meta",
};

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this environment"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const store of Object.values(STORES)) {
        if (!db.objectStoreNames.contains(store)) {
          // aiHistory is append-only with auto keys; everything else keys by id.
          if (store === STORES.aiHistory) {
            db.createObjectStore(store, { keyPath: "id", autoIncrement: true });
          } else {
            db.createObjectStore(store, { keyPath: "id" });
          }
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Failed to open demo IndexedDB"));
  });
  return dbPromise;
}

function tx(db, storeName, mode) {
  return db.transaction(storeName, mode).objectStore(storeName);
}

function wrap(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function idbGetAll(storeName) {
  const db = await openDB();
  return wrap(tx(db, storeName, "readonly").getAll());
}

export async function idbGet(storeName, id) {
  const db = await openDB();
  return wrap(tx(db, storeName, "readonly").get(id));
}

export async function idbPut(storeName, value) {
  const db = await openDB();
  await wrap(tx(db, storeName, "readwrite").put(value));
  return value;
}

export async function idbBulkPut(storeName, values = []) {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    for (const v of values) store.put(v);
  });
  return values;
}

export async function idbDelete(storeName, id) {
  const db = await openDB();
  return wrap(tx(db, storeName, "readwrite").delete(id));
}

export async function idbClear(storeName) {
  const db = await openDB();
  return wrap(tx(db, storeName, "readwrite").clear());
}

export async function idbClearAll() {
  const db = await openDB();
  await Promise.all(
    Object.values(STORES).map(
      (store) =>
        new Promise((resolve, reject) => {
          const r = tx(db, store, "readwrite").clear();
          r.onsuccess = () => resolve();
          r.onerror = () => reject(r.error);
        })
    )
  );
}
